import { AreaDto, AreaGroupDto } from 'services/client-api-wrapper/campus_api/responses.dto';
import { IRoomResource } from 'kits/apiKit3/legacy';
import IvicosStrings from 'kits/language/stringKit';
import { DefaultValue, selector, selectorFamily } from 'recoil';
import { aAreaGroupList, aAreaList, aRoomList, aRoomListStyles } from 'shared-state/directory/atoms';
import { aCurrentAreaId } from 'shared-state/location/atoms';
import { sUsers } from 'shared-state/presence/selectors';
import { aUsersAndVisitorsNumber } from 'shared-state/presence/atoms';

export const sStyledRoomList = selector<IRoomResource[]>({
    key: 'sStyledRoomList',
    get: ({ get }) => {
        //TODO sort
        const roomList = get(aRoomList);
        const styles = get(aRoomListStyles);

        if (!roomList) return [];

        const nextStylesList = roomList.map((room: IRoomResource) => {
            const areaCheck = !styles.highlightFromArea || styles.highlightFromArea == room.relationships.area;
            const visitorCheck = !styles.highlightForVisitors || room.attributes.openForVisitors;

            return {
                ...room,
                ...{
                    attributes: {
                        ...room.attributes,
                        ...{ dimmed: !(areaCheck && visitorCheck) }
                    }
                }
            };
        });

        return nextStylesList;
    }
});

export const sPersonalRoomList = selector<IRoomResource[]>({
    key: 'sPersonalRoomList',
    get: ({ get }) => {
        const areaId = get(aCurrentAreaId);
        const userList = get(sUsers);

        const usersInPersonalRooms = userList.filter((u) => u.room?.includes('personal-'));
        const personalRoomIds = Array.from(new Set(usersInPersonalRooms.map((u) => u.room)));

        const nextPersonalRoomList: IRoomResource[] = personalRoomIds.map((roomId) => ({
            id: roomId || '-',
            type: 'iv_campus_room',
            attributes: {
                variant: 'DEFAULT',
                displayName:
                    // eslint-disable-next-line quotes
                    userList.find((u) => u.uid == roomId?.replace('personal-', '').split('_')[0])?.displayName + IvicosStrings.personalRoomTitle ||
                    IvicosStrings.unknownPersonalRoomTitle,
                private: true,
                symbol: 'Room'
            },
            relationships: {
                area: areaId || '-',
                owner: roomId?.replace('personal-', '').split('_')[0] || '*'
            }
        }));

        return nextPersonalRoomList;
    }
});

export const sRoom = selectorFamily<IRoomResource | undefined, string>({
    key: 'sRoom',
    get:
        (roomId: string) =>
        ({ get }) => {
            const roomList = get(sStyledRoomList);
            const personalRoomList = get(sPersonalRoomList);
            return [...roomList, ...personalRoomList].find((room) => room.id == roomId);
        },
    set:
        (roomId: string) =>
        ({ set, get }, newValue) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const roomList = get(aRoomList);
            if (!newValue) return set(aRoomList, (prevRoomList) => prevRoomList.filter((r) => r.id != roomId));
            if (newValue instanceof DefaultValue) return;

            set(aRoomList, (prevRoomList) => {
                const room$ = prevRoomList.find((room) => room.id == roomId);
                if (room$)
                    return prevRoomList.map((room) => {
                        if (room.id != roomId) return room;

                        return newValue;
                    });

                return [...prevRoomList, ...[newValue]];
            });
        }
});

export const sRoomListIncludingPersonalRooms = selector<IRoomResource[]>({
    key: 'sRoomListIncludingPersonalRooms',
    get: ({ get }) => {
        const roomList = get(sStyledRoomList);
        const personalRoomList = get(sPersonalRoomList);
        return [...roomList, ...personalRoomList];
    }
});

export const sArea = selectorFamily<AreaDto | undefined, string | undefined>({
    key: 'sArea',
    get:
        (areaId: string | undefined) =>
        ({ get }) => {
            if (!areaId) return undefined;
            const areaList = get(aAreaList);
            if (!Array.isArray(areaList)) {
                console.warn('areaList is not an array');
                return undefined;
            }
            return areaList.find((a) => a.id == areaId);
        },
    set:
        (areaId: string | undefined) =>
        ({ set, get }, newValue) => {
            if (!areaId) return;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const areaList = get(aAreaList);
            if (!newValue) return set(aAreaList, (prevAreaList) => prevAreaList.filter((a) => a.id != areaId));
            if (newValue instanceof DefaultValue) return;

            set(aAreaList, (prevAreaList) => {
                const room$ = prevAreaList.find((area) => area.id == areaId);
                if (room$)
                    return prevAreaList.map((area) => {
                        if (area.id != areaId) return area;

                        return newValue;
                    });

                return [...prevAreaList, ...[newValue]];
            });
        }
});

export const sAreaGroup = selectorFamily<AreaGroupDto | undefined, string | undefined>({
    key: 'sAreaGroup',
    get:
        (areaGroupId: string | undefined) =>
        ({ get }) => {
            if (!areaGroupId) return undefined;
            const areaGroupList = get(aAreaGroupList);
            if (!Array.isArray(areaGroupList)) {
                console.warn('areaGroupList is not an array');
                return undefined;
            }

            return areaGroupList.find((ag) => ag.id == areaGroupId);
        },
    set:
        (areaGroupId: string | undefined) =>
        ({ set, get }, newValue) => {
            if (!areaGroupId) return;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const areaGroupList = get(aAreaGroupList);
            if (!newValue) return set(aAreaGroupList, (prevAreaGroupList) => prevAreaGroupList.filter((ag) => ag.id != areaGroupId));
            if (newValue instanceof DefaultValue) return;

            set(aAreaGroupList, (prevAreaGroupList) => {
                const areaGroup$ = prevAreaGroupList.find((areaGroup) => areaGroup.id == areaGroupId);
                if (areaGroup$)
                    return prevAreaGroupList.map((areaGroup) => {
                        if (areaGroup.id != areaGroupId) return areaGroup;

                        return newValue;
                    });

                return [...prevAreaGroupList, ...[newValue]];
            });
        }
});

export const sAreaListWithCount = selector<AreaDto[]>({
    key: 'sAreaListWithCount',
    get: ({ get }) => {
        const areaList = get(aAreaList);
        const usersAndVisitorsNumber = get(aUsersAndVisitorsNumber);

        if (!Array.isArray(areaList)) {
            console.warn('areaList is not an array');
            return [];
        }

        const areaListWithCount = areaList.map((area) => {
            const count = usersAndVisitorsNumber[area.id] || { users: 0, visitors: 0 };
            return { ...area, count };
        });

        return areaListWithCount;
    }
});

export const sAreaGroupListWithCount = selector<AreaGroupDto[]>({
    key: 'sAreaGroupListWithCount',
    get: ({ get }) => {
        const areaGroupList = get(aAreaGroupList);
        const usersAndVisitorsNumber = get(aUsersAndVisitorsNumber);

        if (!Array.isArray(areaGroupList)) {
            console.warn('areaGroupList is not an array');
            return [];
        }

        // Helper function to recursively calculate counts for an area group
        const calculateAreaGroupCount = (areaGroup: AreaGroupDto): { users: number; visitors: number } => {
            let totalUsers = 0;
            let totalVisitors = 0;

            // Add counts from direct included areas
            for (const areaId of areaGroup.includedAreaIds) {
                const areaCount = usersAndVisitorsNumber[areaId] || { users: 0, visitors: 0 };
                totalUsers += areaCount.users;
                totalVisitors += areaCount.visitors;
            }

            // Add counts from child area groups (recursive)
            for (const childAreaGroup of areaGroupList) {
                if (childAreaGroup.parentAreaId === areaGroup.id) {
                    const childCount = calculateAreaGroupCount(childAreaGroup);
                    totalUsers += childCount.users;
                    totalVisitors += childCount.visitors;
                }
            }

            return { users: totalUsers, visitors: totalVisitors };
        };

        const areaGroupListWithCount = areaGroupList.map((areaGroup) => {
            const count = calculateAreaGroupCount(areaGroup);
            return { ...areaGroup, count };
        });

        return areaGroupListWithCount;
    }
});
